import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { User, OrganizationRole } from '../../../models/user.model';
import { ApiService } from '../../http/api.service';
import { BROWSER_STORAGE, BrowserStorageService } from '../../services/storage.service';
import { JwtService } from '../../services/jwt.service';
import { Organization } from '../../../models/organization.model';
import { OrganizationService } from '../../services/organization.service';
import { NotificationService } from '../../services/notification.service';
import { InvitesService } from '../../services/invites.service';
import { NavigationService } from '../../services/navigation.service';
import { ToastService } from '../../services/toast.service';
import { CartService } from '../../../modules/store/services/cart.service';
import { WebSocketService } from '../../../modules/support-shared/services/websocket.service';
import { CoachingRealtimeService } from '../../../modules/coaching/services/coaching-realtime.service';
interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  organizationId?: string;
  recaptchaToken: string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  recaptchaToken: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private currentOrgRoleSubject = new BehaviorSubject<OrganizationRole | null>(null);
  private currentRolesSubject = new BehaviorSubject<string[] | null>(null);
  private currentOrganizationSubject = new BehaviorSubject<Organization | null>(null);
  private tokenValidated = new BehaviorSubject<boolean>(false);
  private hasCompletedOnboardingSubject = new BehaviorSubject<boolean>(false);
  hasCompletedOnboarding$ = this.hasCompletedOnboardingSubject.asObservable();
  currentUser$ = this.currentUserSubject.asObservable();
  currentOrgRole$ = this.currentOrgRoleSubject.asObservable();
  currentRoles$ = this.currentRolesSubject.asObservable();
  currentOrganization$ = this.currentOrganizationSubject.asObservable();
  private tokenValidated$ = this.tokenValidated.asObservable();

  private tokenData = new BehaviorSubject<any>(null);
  tokenData$ = this.tokenData.asObservable();

  constructor(
    private api: ApiService, 
    private localStorage: BrowserStorageService, 
    private jwtService: JwtService,
    private injector: Injector
  ) {
    this.loadStoredAuth();
  }

  initialize(): Observable<User | null> {
    return this.validateToken();
  }

  private loadStoredAuth(): void {
    const storedUser = this.localStorage.getItem('user');
    const storedOrgRole = this.localStorage.getItem('orgRole');
    const onboardingCompleted = this.localStorage.getItem('onboardingCompleted');

    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
    if (storedOrgRole) {
      this.currentOrgRoleSubject.next(JSON.parse(storedOrgRole));
    }
    if (onboardingCompleted && onboardingCompleted !== 'null' && onboardingCompleted !== 'undefined') {
      this.hasCompletedOnboardingSubject.next(onboardingCompleted === 'true');
    }
    const storedToken = this.getToken();
    if (storedToken) {
      try {
        this.tokenData.next(this.jwtService.decodeToken(storedToken));
      } catch {
        this.tokenData.next(null);
      }
    }
    this.validateToken();
  }

  calculateAgeAndQuarter(dateOfBirth: Date): any {
    const today = new Date();

    let currentYear = new Date().getFullYear();
    // Calculate current age
    let currentAge = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
      currentAge--;
    }

    // Calculate running age (age at the start of the year)
    let runningAge = currentAge;
    //if (today.getMonth() < dateOfBirth.getMonth() ||
    //  (today.getMonth() === dateOfBirth.getMonth() && today.getDate() < dateOfBirth.getDate())) {
      runningAge++;
    //}

    // Calculate quarters for the current year
    const birthMonth = dateOfBirth.getMonth();
    const birthDate = dateOfBirth.getDate();
    const currentMonth = today.getMonth();

    // Calculate quarter start and end dates
    const quarterStartDate = new Date(currentYear, birthMonth, birthDate);
    
    if (quarterStartDate > today) {
      currentYear = currentYear - 1;
    }
    const quarterEndDate = new Date(currentYear, birthMonth + 3, birthDate - 1);

    // Determine current quarter
    let quarterNumber = 1;
    let quarterStart = new Date(currentYear, birthMonth, birthDate);
    let quarterEnd = new Date(currentYear, birthMonth + 3, birthDate - 1);

    for (let i = 0; i < 4; i++) {
      if (today >= quarterStart && today <= quarterEnd) {
        quarterNumber = i + 1;
        break;
      }
      quarterStart = new Date(currentYear, birthMonth + (i + 1) * 3, birthDate);
      quarterEnd = new Date(currentYear, birthMonth + (i + 2) * 3, birthDate - 1);
    }

    let currentQuarter = `Q${quarterNumber}`;
    let quarterInfo = {
      quarter: currentQuarter,
      startDate: quarterStart,
      endDate: quarterEnd
    };
    currentYear = new Date(quarterStart).getFullYear();


    return {
      currentAge,
      runningAge,
      currentQuarter,
      quarterInfo,
      currentYear
    };
  }

  setCurrentUser({first_name, last_name, email, profile_picture}: {first_name: string, last_name: string, email: string, profile_picture: string}): void {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      currentUser.first_name = first_name;
      currentUser.last_name = last_name;
      currentUser.email = email;
      currentUser.picture_url = profile_picture;
      this.currentUserSubject.next(currentUser);
      this.localStorage.setItem('user', JSON.stringify(currentUser));
    }
  }

  setContemplateYear(result: any): void {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      currentUser.date_of_birth = result.date_of_birth;
      this.currentUserSubject.next(currentUser);
      this.localStorage.setItem('user', JSON.stringify(currentUser));
    }
  }

  login(credentials: LoginCredentials): Observable<User> {
    return this.api.post<User>('auth/email/login', credentials).pipe(
      tap(response => {
        this.setAuthData(response);
      })
    );
  }

  register(data: RegisterData): Observable<User> {
    return this.api.post<User>('auth/email/register', data).pipe(
      tap(response => {
        this.setAuthData(response);
      })
    );
  }

  validateToken(): Observable<User | null> {
    const token = this.getToken();

    if (!token) {
      this.tokenValidated.next(false);
      return of(null);
    }

    return this.api.get<User>('auth/validate-token').pipe(
      tap((response) => {
        this.setAuthData(response);
        this.tokenValidated.next(true);
      }),
      catchError((error) => {
        console.log('error', error);
        this.tokenValidated.next(false);
        this.localStorage.removeItem('user');
        this.localStorage.removeItem('orgRole');
        this.localStorage.removeItem('roles');
        this.localStorage.removeItem('token');
        this.localStorage.removeItem('onboardingCompleted');
        return of(null);
      })
    );
  }

  /**
   * Re-issue JWT with current subscription/role claims from the API.
   * Use after affiliation code join or payment success — not on every validate-token.
   */
  refreshSession(): Observable<User | null> {
    const token = this.getToken();
    if (!token) {
      return of(null);
    }

    return this.api.post<User>('auth/refresh-session', {}).pipe(
      tap((response) => {
        this.setAuthData(response);
        this.tokenValidated.next(true);
      }),
      catchError((error) => {
        console.log('refreshSession error', error);
        return of(null);
      })
    );
  }

  isTokenValidated(): Observable<boolean> {
    return this.tokenValidated$;
  }

  forgotPassword(payload: { email: string; recaptchaToken: string }): Observable<void> {
    return this.api.post<void>('auth/email/forgot-password', payload);
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.api.post<void>('auth/email/reset-password', { token, newPassword });
  }

  verifyEmail(token: string): Observable<void> {
    return this.api.post<void>('auth/email/verify', { token });
  }

  resendVerification(): Observable<void> {
    return this.api.post<void>('auth/email/resend-verification', { email: this.currentUser?.email });
  }

  switchOrganization(organizationId: string): void {
    this.localStorage.removeItem('token');
    this.localStorage.removeItem('orgRole');
    this.currentOrgRoleSubject.next(null);
  }

  logout(): Observable<null> {
    // Close WebSocket connections first
    try {
      const webSocketService = this.injector.get(WebSocketService, null);
      if (webSocketService) {
        webSocketService.closeAllConnections();
      }
    } catch (e) {
      // WebSocketService might not be available, continue
    }

    try {
      const coachingRealtime = this.injector.get(CoachingRealtimeService, null);
      coachingRealtime?.stop();
    } catch (e) {
      // Coaching realtime might not be available, continue
    }

    // Reset all service states
    try {
      const organizationService = this.injector.get(OrganizationService, null);
      const notificationService = this.injector.get(NotificationService, null);
      const cartService = this.injector.get(CartService, null);
      const invitesService = this.injector.get(InvitesService, null);
      const navigationService = this.injector.get(NavigationService, null);
      const toastService = this.injector.get(ToastService, null);

      if (organizationService) organizationService.reset();
      if (notificationService) notificationService.clearAll();
      if (cartService) cartService.reset();
      if (invitesService) invitesService.reset();
      if (navigationService) navigationService.reset();
      if (toastService) toastService.clear();
    } catch (e) {
      // Some services might not be available, continue
      console.warn('Error resetting services on logout:', e);
    }

    // Reset all BehaviorSubjects
    this.currentUserSubject.next(null);
    this.currentOrgRoleSubject.next(null);
    this.currentRolesSubject.next(null);
    this.currentOrganizationSubject.next(null);
    this.tokenValidated.next(false);
    this.tokenData.next(null);

    try {
      sessionStorage.removeItem('subscribePromptShown');
    } catch {
      // Ignore storage errors during logout
    }
    
    // Clear localStorage
    this.localStorage.clear();
    
    return of(null);
  }

  public setAuthData(response: any): void {
    const { user, token, roles, hasCompletedOnboarding } = response.data;
    this.localStorage.setItem('token', token);

    const tokenData = this.jwtService.decodeToken(token);
    this.tokenData.next(tokenData);
    if (hasCompletedOnboarding !== undefined && hasCompletedOnboarding !== null) {
      this.hasCompletedOnboardingSubject.next(!!hasCompletedOnboarding);
      this.localStorage.setItem('onboardingCompleted', hasCompletedOnboarding ? 'true' : 'false');
    }
    if (user) {
      this.localStorage.setItem('user', JSON.stringify(user));
      this.currentUserSubject.next(user);
    }
    if (roles) {
      this.localStorage.setItem('roles', JSON.stringify(roles));
      this.currentOrgRoleSubject.next(roles);
    }
  }

  getHasCompletedOnboarding(): boolean {
    return this.hasCompletedOnboardingSubject.value;
  }

  getToken(): string | null {
    return this.localStorage.getItem('token');
  }

  getTokenData(): any {
    return this.tokenData.value;
  }

  getRoles(): string[] {
    const tokenRoles = this.getTokenData()?.roles;
    if (Array.isArray(tokenRoles) && tokenRoles.length) {
      return tokenRoles;
    }
    try {
      const stored = this.localStorage.getItem('roles');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  get currentUserId(): string | null {
    return this.currentUser?.id || null;
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    console.log("token", token);
    if (token) {
      const remainingTime = this.jwtService.getRemainingTime(token);
      console.log("Remaining time: ", remainingTime);
      if (remainingTime <= 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

}
